/**
 * Logging utilities for content parser
 * Centralized logging with debug toggle for easier control
 */

// Debug toggle - set to false to disable all logging
const DEBUG_CONTENT_PARSER = process.env.NODE_ENV === 'development';

/**
 * Log if debugging is enabled
 */
const log = (message: string, ...args: any[]) => {
  if (DEBUG_CONTENT_PARSER) {
    // console.log(message, ...args);
  }
};

/**
 * Log error (always logged, regardless of debug toggle)
 */
const logError = (message: string, ...args: any[]) => {
  // console.error(message, ...args);
};

// Parse JSON safely logging
export const logParseJSONSafelyStart = (length: number, preview: string) => {
  log('🔧 parseJSONSafely - Input length:', length);
  log('🔧 parseJSONSafely - First 300 chars:', preview);
};

export const logParseJSONSafelySuccess = () => {
  log('✅ parseJSONSafely - Direct parse succeeded');
};

export const logParseJSONSafelyFailed = (error: any) => {
  log('❌ parseJSONSafely - Direct parse failed:', error.message);
};

export const logParseJSONSafelyEscaped = (preview: string) => {
  log('🔧 parseJSONSafely - Escaped first 300 chars:', preview);
};

export const logParseJSONSafelyEscapedSuccess = () => {
  log('✅ parseJSONSafely - Escaped parse succeeded');
};

export const logParseJSONSafelyEscapedFailed = (error: any, position?: string) => {
  logError('❌ parseJSONSafely - Escaped parse also failed:', error.message);
  if (position) {
    logError('❌ parseJSONSafely - Error at position:', position);
  }
};

// Parse generated content structure logging
export const logParseGeneratedContentStructureStart = (data: any) => {
  log('📋 parseGeneratedContentStructure - START');
  log('📋 parseGeneratedContentStructure - Input type:', typeof data);
  log('📋 parseGeneratedContentStructure - Input keys:', typeof data === 'object' && data !== null ? Object.keys(data) : 'N/A');
  log('📋 parseGeneratedContentStructure - Full input:', JSON.stringify(data, null, 2));
};

export const logParseGeneratedContentStructureNotObject = () => {
  log('❌ parseGeneratedContentStructure - Not an object');
};

export const logParseGeneratedContentStructureHasPlatformKeys = () => {
  log('✅ parseGeneratedContentStructure - Has platform keys');
};

export const logParseGeneratedContentStructureNoPlatformKeys = () => {
  log('❌ parseGeneratedContentStructure - No platform keys found');
};

// Extract generated content logging
export const logExtractGeneratedContentStart = (generatedContent: any) => {
  log('📥 extractGeneratedContent - START');
  log('📥 extractGeneratedContent - Full input:', JSON.stringify(generatedContent, null, 2));
};

export const logExtractGeneratedContentString = (length: number, preview: string) => {
  log('📥 extractGeneratedContent - Content is string, length:', length);
  log('📥 extractGeneratedContent - String preview:', preview);
};

export const logExtractGeneratedContentEnd = (content: any) => {
  log('📥 extractGeneratedContent - Final content:', JSON.stringify(content, null, 2));
  log('📥 extractGeneratedContent - END');
};

// Parse variation logging
export const logParseVariationStart = (platformKey?: string, variationKey?: string, type?: string) => {
  log('🔍 parseVariation - START', { platformKey, variationKey, type });
};

export const logParseVariationInput = (variation: any) => {
  log('🔍 parseVariation - Input:', JSON.stringify(variation, null, 2));
};

export const logParseVariationFoundObject = () => {
  log('✅ parseVariation - Found variation object');
};

export const logParseVariationJSONString = () => {
  log('🔧 parseVariation - Content appears to be JSON string, attempting to parse');
};

export const logParseVariationNestedSuccess = () => {
  log('✅ parseVariation - Successfully parsed nested JSON content');
};

export const logParseVariationExtractedFromContent = () => {
  log('✅ parseVariation - Extracted from content structure');
};

export const logParseVariationExtractedFromPlatform = () => {
  log('✅ parseVariation - Extracted from platform structure');
};

// Parse platform content logging
export const logParsePlatformContentStart = (platformKey?: string, type?: string) => {
  log('📱 parsePlatformContent - START', { platformKey, type });
};

export const logParsePlatformContentInput = (platformContent: any) => {
  log('📱 parsePlatformContent - Input:', JSON.stringify(platformContent, null, 2));
};

export const logParsePlatformContentNestedJSON = () => {
  log('🔧 parsePlatformContent - variation_1.content is a JSON string, extracting all variations from nested structure');
};

export const logParsePlatformContentExtractedLength = (length: number) => {
  log(`🔧 parsePlatformContent - Extracted JSON length: ${length}`);
};

export const logParsePlatformContentParsedKeys = (keys: string[]) => {
  log(`🔧 parsePlatformContent - Parsed JSON has keys:`, keys);
};

export const logParsePlatformContentExtractedPlatform = () => {
  log('✅ parsePlatformContent - Extracted platform data from nested JSON, finding all variations');
};

export const logParsePlatformContentVariationsFound = (count: number) => {
  log(`✅ parsePlatformContent - Found ${count} variations from nested JSON`);
};

export const logParsePlatformContentPlatformDataNotObject = (type: string) => {
  log('❌ parsePlatformContent - Platform data is not an object:', type);
};

export const logParsePlatformContentKeyNotFound = (platformKey: string, availableKeys: string[]) => {
  log(`❌ parsePlatformContent - Platform key '${platformKey}' not found in parsed JSON. Available keys:`, availableKeys);
};

export const logParsePlatformContentNoPlatformKey = () => {
  log('❌ parsePlatformContent - No platformKey provided');
};

export const logParsePlatformContentParseFailed = () => {
  log('❌ parsePlatformContent - Failed to parse extracted JSON or result is not an object');
};

export const logParsePlatformContentExtractFailed = (preview: string) => {
  log('❌ parsePlatformContent - Failed to extract JSON object from content');
  log('🔧 parsePlatformContent - Content preview (first 500 chars):', preview);
};

export const logParsePlatformContentFallbackExtracted = (length: number) => {
  log('🔧 parsePlatformContent - Fallback: Extracted JSON using alternative method, length:', length);
};

export const logParsePlatformContentFallbackExtractedPlatform = () => {
  log('✅ parsePlatformContent - Fallback: Extracted platform data, finding variations');
};

export const logParsePlatformContentFallbackVariationsFound = (count: number) => {
  log(`✅ parsePlatformContent - Fallback: Found ${count} variations`);
};

export const logParsePlatformContentTopLevelVariations = (count: number, keys: string[]) => {
  log(`🔍 parsePlatformContent - Found ${count} top-level variations:`, keys);
};

export const logParsePlatformContentNoTopLevelVariations = () => {
  log('🔧 parsePlatformContent - No top-level variations found, checking for nested JSON in variation_1.content');
};

export const logParsePlatformContentEnd = (variationsCount: number) => {
  log('📱 parsePlatformContent - END, variations count:', variationsCount);
};

// Parse variation content logging
export const logParseVariationContentStart = (platformKey: string, variationKey: string, contentLength: number) => {
  log('🔄 parseVariationContent - START', { platformKey, variationKey, contentLength });
};

export const logParseVariationContentPreview = (preview: string) => {
  log('🔄 parseVariationContent - Content preview:', preview);
};

export const logParseVariationContentStrippingMarkdown = () => {
  log('🔧 parseVariationContent - Stripping markdown code blocks');
};

export const logParseVariationContentAfterStripping = (preview: string) => {
  log('🔧 parseVariationContent - After stripping, preview:', preview);
};

export const logParseVariationContentNoJSON = () => {
  log('❌ parseVariationContent - No JSON object found');
};

export const logParseVariationContentParseFailed = () => {
  log('❌ parseVariationContent - Failed to parse JSON');
};

export const logParseVariationContentParsedJSON = (parsed: any) => {
  log('🔄 parseVariationContent - Parsed JSON:', JSON.stringify(parsed, null, 2));
};

export const logParseVariationContentExtractedFromPlatform = () => {
  log('✅ parseVariationContent - Extracted variation from platform structure');
};

export const logParseVariationContentExtractedFromDirect = () => {
  log('✅ parseVariationContent - Extracted content from direct object');
};

export const logParseVariationContentNoMatch = () => {
  log('❌ parseVariationContent - No matching variation found');
};

